// Rails ActiveSupport timezone names → IANA timezone identifiers
const RAILS_TO_IANA = {
  // Europe
  'Moscow': 'Europe/Moscow',
  'Kaliningrad': 'Europe/Kaliningrad',
  'Minsk': 'Europe/Minsk',
  'Kiev': 'Europe/Kyiv',
  'Kyiv': 'Europe/Kyiv',
  'Helsinki': 'Europe/Helsinki',
  'Riga': 'Europe/Riga',
  'Tallinn': 'Europe/Tallinn',
  'Vilnius': 'Europe/Vilnius',
  'Berlin': 'Europe/Berlin',
  'Paris': 'Europe/Paris',
  'Amsterdam': 'Europe/Amsterdam',
  'Brussels': 'Europe/Brussels',
  'Vienna': 'Europe/Vienna',
  'Warsaw': 'Europe/Warsaw',
  'Prague': 'Europe/Prague',
  'Budapest': 'Europe/Budapest',
  'Bucharest': 'Europe/Bucharest',
  'Sofia': 'Europe/Sofia',
  'Athens': 'Europe/Athens',
  'Istanbul': 'Europe/Istanbul',
  'London': 'Europe/London',
  'Dublin': 'Europe/Dublin',
  'Lisbon': 'Europe/Lisbon',
  'Madrid': 'Europe/Madrid',
  'Rome': 'Europe/Rome',
  'Stockholm': 'Europe/Stockholm',
  'Oslo': 'Europe/Oslo',
  'Copenhagen': 'Europe/Copenhagen',
  'Zurich': 'Europe/Zurich',
  'Belgrade': 'Europe/Belgrade',
  'Bratislava': 'Europe/Bratislava',
  'Ljubljana': 'Europe/Ljubljana',
  'Sarajevo': 'Europe/Sarajevo',
  'Skopje': 'Europe/Skopje',
  'Zagreb': 'Europe/Zagreb',
  // Russia / CIS
  'Samara': 'Europe/Samara',
  'Ekaterinburg': 'Asia/Yekaterinburg',
  'Novosibirsk': 'Asia/Novosibirsk',
  'Krasnoyarsk': 'Asia/Krasnoyarsk',
  'Irkutsk': 'Asia/Irkutsk',
  'Yakutsk': 'Asia/Yakutsk',
  'Vladivostok': 'Asia/Vladivostok',
  'Sakhalin': 'Asia/Sakhalin',
  'Magadan': 'Asia/Magadan',
  'Srednekolymsk': 'Asia/Srednekolymsk',
  'Kamchatka': 'Asia/Kamchatka',
  'Almaty': 'Asia/Almaty',
  'Astana': 'Asia/Almaty',
  'Tashkent': 'Asia/Tashkent',
  'Baku': 'Asia/Baku',
  'Yerevan': 'Asia/Yerevan',
  'Tbilisi': 'Asia/Tbilisi',
  // Asia
  'Dubai': 'Asia/Dubai',
  'Jerusalem': 'Asia/Jerusalem',
  'Riyadh': 'Asia/Riyadh',
  'Baghdad': 'Asia/Baghdad',
  'Tehran': 'Asia/Tehran',
  'Karachi': 'Asia/Karachi',
  'Kolkata': 'Asia/Kolkata',
  'Dhaka': 'Asia/Dhaka',
  'Bangkok': 'Asia/Bangkok',
  'Jakarta': 'Asia/Jakarta',
  'Singapore': 'Asia/Singapore',
  'Kuala Lumpur': 'Asia/Kuala_Lumpur',
  'Hong Kong': 'Asia/Hong_Kong',
  'Beijing': 'Asia/Shanghai',
  'Shanghai': 'Asia/Shanghai',
  'Taipei': 'Asia/Taipei',
  'Seoul': 'Asia/Seoul',
  'Tokyo': 'Asia/Tokyo',
  // Americas
  'Eastern Time (US & Canada)': 'America/New_York',
  'Central Time (US & Canada)': 'America/Chicago',
  'Mountain Time (US & Canada)': 'America/Denver',
  'Pacific Time (US & Canada)': 'America/Los_Angeles',
  'Alaska': 'America/Anchorage',
  'Hawaii': 'Pacific/Honolulu',
  'Atlantic Time (Canada)': 'America/Halifax',
  'Newfoundland': 'America/St_Johns',
  'Bogota': 'America/Bogota',
  'Lima': 'America/Lima',
  'Santiago': 'America/Santiago',
  'Buenos Aires': 'America/Argentina/Buenos_Aires',
  'Brasilia': 'America/Sao_Paulo',
  'Mexico City': 'America/Mexico_City',
  'Guadalajara': 'America/Mexico_City',
  'Monterrey': 'America/Monterrey',
  // Africa / Other
  'Cairo': 'Africa/Cairo',
  'Nairobi': 'Africa/Nairobi',
  'Johannesburg': 'Africa/Johannesburg',
  'UTC': 'UTC',
};

/**
 * Converts a Rails/ActiveSupport timezone name to an IANA timezone string.
 * Falls back to the browser's local timezone if the name is unknown.
 *
 * @param {string|null} railsTzName - e.g. "Moscow", "Berlin"
 * @returns {string} IANA timezone, e.g. "Europe/Moscow"
 */
export function railsToIana(railsTzName) {
  if (!railsTzName) return Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (RAILS_TO_IANA[railsTzName]) return RAILS_TO_IANA[railsTzName];

  // May already be a valid IANA identifier (e.g. "Europe/Moscow")
  try {
    Intl.DateTimeFormat(undefined, { timeZone: railsTzName });
    return railsTzName;
  } catch {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

/**
 * Returns the short timezone abbreviation for the given IANA zone
 * at a specific reference date.
 *
 * @param {string} ianaZone   - IANA timezone, e.g. "Europe/Moscow"
 * @param {Date}   [refDate]  - reference date for DST context (defaults to now)
 * @returns {string} e.g. "MSK", "CET", "EST"
 */
export function getTzAbbr(ianaZone, refDate = new Date()) {
  return new Intl.DateTimeFormat('en', {
    timeZone: ianaZone,
    timeZoneName: 'short',
  })
    .formatToParts(refDate)
    .find(p => p.type === 'timeZoneName')?.value || '';
}

/**
 * Converts an "HH:MM" time string from one IANA timezone to another.
 *
 * @param {string} timeStr      - "08:15"
 * @param {string} sourceIana   - source IANA timezone, e.g. "Europe/Paris"
 * @param {string} targetIana   - target IANA timezone, e.g. "Europe/Moscow"
 * @param {Date}   [refDate]    - reference date to resolve DST correctly;
 *                                defaults to a stable winter date (2027-01-06)
 * @returns {string} converted time string "HH:MM"
 */
export function convertTime(timeStr, sourceIana, targetIana, refDate) {
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Build a UTC timestamp by interpreting the time in the source timezone.
  // Strategy: take a reference date, set the wall-clock time in sourceIana,
  // derive UTC, then format in targetIana.
  const base = refDate ?? new Date(Date.UTC(2027, 0, 6)); // stable winter date

  // Format the base date as a date-only string in the source timezone
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: sourceIana,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(base);
  const year  = dateParts.find(p => p.type === 'year').value;
  const month = dateParts.find(p => p.type === 'month').value;
  const day   = dateParts.find(p => p.type === 'day').value;

  // Construct an ISO string treated as local time in the source timezone
  const localIso = `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

  // Parse it back to UTC using the Temporal-like trick via Intl
  const utcMs = Date.parse(
    new Intl.DateTimeFormat('sv', {
      timeZone: sourceIana,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(new Date(localIso + 'Z')).replace(' ', 'T') + 'Z'
  );

  // Simpler and reliable: offset-based approach
  const probe = new Date(`${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00Z`);

  // Get the UTC offset of sourceIana at the reference date
  const sourceOffset = getUtcOffsetMinutes(sourceIana, base);
  // Adjust probe to true UTC
  const utcDate = new Date(probe.getTime() - sourceOffset * 60_000);

  return utcDate.toLocaleTimeString('ru-RU', {
    timeZone: targetIana,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Returns the UTC offset in minutes for an IANA timezone at a given date.
 * Positive = east of UTC (e.g. Moscow = +180).
 *
 * @param {string} ianaZone
 * @param {Date}   date
 * @returns {number} offset in minutes
 */
export function getUtcOffsetMinutes(ianaZone, date = new Date()) {
  const utcStr  = date.toLocaleString('en-CA', { timeZone: 'UTC',     hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const localStr = date.toLocaleString('en-CA', { timeZone: ianaZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const parse = s => {
    const [datePart, timePart] = s.split(', ');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    return new Date(Date.UTC(y, mo - 1, d, h, mi));
  };

  return (parse(localStr) - parse(utcStr)) / 60_000;
}

/**
 * Returns the day-of-week (0=Sun … 6=Sat) and current HH:MM for the given
 * IANA timezone at a specific moment.
 */
function getLocalDayAndTime(ianaZone, date) {
  const datePart = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en', {
    timeZone: ianaZone,
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).replace(/^24/, '00'); // midnight edge-case

  const [h, m] = timePart.split(':').map(Number);

  return {
    dow: new Date(datePart + 'T00:00:00').getDay(), // 0=Sun … 6=Sat
    hour: h,
    minute: m,
  };
}

/**
 * Finds the nearest upcoming race slot within a weekly schedule.
 *
 * @param {Object<string, string[]>} schedule  - { dayKey: ["HH:MM", ...], ... }
 * @param {Object<string, number>}   dowMap    - { dayKey: dayOfWeek (0–6) }
 * @param {string}                   sourceIana - IANA timezone in which times are defined
 * @param {Date}                     [now]      - current moment (defaults to Date.now())
 * @returns {{ dayKey: string, timeIndex: number, minutesUntil: number } | null}
 */
export function findNextRaceSlot(schedule, dowMap, sourceIana, now = new Date()) {
  const { dow: nowDow, hour: nowHour, minute: nowMinute } =
    getLocalDayAndTime(sourceIana, now);

  const nowMinOfWeek = nowDow * 1440 + nowHour * 60 + nowMinute;

  let nearest = null;
  let nearestMinutes = Infinity;

  for (const [dayKey, times] of Object.entries(schedule)) {
    const targetDow = dowMap[dayKey];
    if (targetDow === undefined) continue;

    for (let i = 0; i < times.length; i++) {
      const [h, m] = times[i].split(':').map(Number);
      const slotMinOfWeek = targetDow * 1440 + h * 60 + m;

      let minutesUntil = slotMinOfWeek - nowMinOfWeek;
      if (minutesUntil <= 0) minutesUntil += 7 * 1440; // wrap to next week

      if (minutesUntil < nearestMinutes) {
        nearestMinutes = minutesUntil;
        nearest = { dayKey, timeIndex: i, minutesUntil };
      }
    }
  }

  return nearest;
}

/**
 * Converts a named schedule from a source IANA timezone to a target IANA timezone.
 *
 * @param {Object<string, string[]>} schedule  - { dayKey: ["HH:MM", ...], ... }
 * @param {string}                  sourceIana - source IANA timezone
 * @param {string}                  targetIana - target IANA timezone
 * @param {Date}                    [refDate]  - DST reference date
 * @returns {{ converted: Object<string, string[]>, tzAbbr: string }}
 */
export function convertSchedule(schedule, sourceIana, targetIana, refDate) {
  const ref = refDate ?? new Date(Date.UTC(2027, 0, 6));
  const converted = {};
  for (const [day, times] of Object.entries(schedule)) {
    converted[day] = times.map(t => convertTime(t, sourceIana, targetIana, ref));
  }
  return {
    converted,
    tzAbbr: getTzAbbr(targetIana, ref),
  };
}

/**
 * Converts a schedule to the target timezone, tracking day-of-week shifts.
 * Returns slots grouped by local day of week.
 *
 * @returns {{ slots: Array, days: number[], tzAbbr: string }}
 *   slots: [{ sourceDayKey, sourceTimeIndex, localDow, localTime }]
 *   days: unique local days of week (0–6) sorted ascending
 */
export function convertScheduleWithDays(schedule, dowMap, sourceIana, targetIana, refDate) {
  const ref = refDate ?? new Date(Date.UTC(2027, 0, 6));
  const sourceOffset = getUtcOffsetMinutes(sourceIana, ref);
  const targetOffset = getUtcOffsetMinutes(targetIana, ref);

  const slots = [];
  for (const [dayKey, times] of Object.entries(schedule)) {
    const sourceDow = dowMap[dayKey];
    if (sourceDow === undefined) continue;
    for (let i = 0; i < times.length; i++) {
      const [h, m] = times[i].split(':').map(Number);
      const sourceMinOfWeek = sourceDow * 1440 + h * 60 + m;
      const utcMin = sourceMinOfWeek - sourceOffset;
      const localMin = utcMin + targetOffset;
      const normalized = ((localMin % (7 * 1440)) + 7 * 1440) % (7 * 1440);
      const localDow = Math.floor(normalized / 1440);
      const localHour = Math.floor((normalized % 1440) / 60);
      const localMinute = normalized % 60;
      slots.push({
        sourceDayKey: dayKey,
        sourceTimeIndex: i,
        localDow,
        localTime: `${String(localHour).padStart(2, '0')}:${String(localMinute).padStart(2, '0')}`,
      });
    }
  }

  slots.sort((a, b) => {
    const toMin = s => s.localDow * 1440 + parseInt(s.localTime) * 60 + parseInt(s.localTime.split(':')[1]);
    return toMin(a) - toMin(b);
  });

  const days = [...new Set(slots.map(s => s.localDow))].sort((a, b) => a - b);

  return { slots, days, tzAbbr: getTzAbbr(targetIana, ref) };
}

/**
 * Given an actual race date, finds the nearest matching slot in the schedule
 * (within toleranceMinutes). Returns { dayKey, timeIndex } or null.
 */
export function findSlotForDate(date, schedule, dowMap, sourceIana, toleranceMinutes = 15) {
  const { dow, hour, minute } = getLocalDayAndTime(sourceIana, date);
  const dateMin = dow * 1440 + hour * 60 + minute;

  let best = null;
  let bestDiff = Infinity;

  for (const [dayKey, times] of Object.entries(schedule)) {
    const targetDow = dowMap[dayKey];
    if (targetDow === undefined) continue;
    for (let i = 0; i < times.length; i++) {
      const [h, m] = times[i].split(':').map(Number);
      const slotMin = targetDow * 1440 + h * 60 + m;
      const diff = Math.min(
        Math.abs(dateMin - slotMin),
        7 * 1440 - Math.abs(dateMin - slotMin),
      );
      if (diff < bestDiff && diff <= toleranceMinutes) {
        bestDiff = diff;
        best = { dayKey, timeIndex: i };
      }
    }
  }
  return best;
}
